/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
	Article,
	Clear,
	Close,
	FormatAlignCenter,
	FormatAlignJustify,
	FormatAlignLeft,
	FormatAlignRight,
	FormatBold,
	FormatIndentDecrease,
	FormatIndentIncrease,
	FormatItalic,
	FormatListBulleted,
	FormatListNumbered,
	FormatStrikethrough,
	FormatUnderlined,
	Image,
	Link,
	Preview,
	Redo,
	Undo,
} from '@mui/icons-material';
import {
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	IconButton,
	Tooltip,
} from '@mui/material';
import {
	$getRoot,
	$getSelection,
	$isElementNode,
	$isRangeSelection,
	CAN_REDO_COMMAND,
	CAN_UNDO_COMMAND,
	CLEAR_EDITOR_COMMAND,
	FORMAT_ELEMENT_COMMAND,
	FORMAT_TEXT_COMMAND,
	INDENT_CONTENT_COMMAND,
	OUTDENT_CONTENT_COMMAND,
	REDO_COMMAND,
	SELECTION_CHANGE_COMMAND,
	UNDO_COMMAND,
} from 'lexical';
import {
	INSERT_ORDERED_LIST_COMMAND,
	INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link';
import { INSERT_IMAGE_COMMAND } from './imagePlugin';

import { useCallback, useEffect, useRef, useState } from 'react';
import { $findMatchingParent } from '@lexical/utils';
import { getSelectedNode } from '../utils/getSelectedNode';
import { ConfirmDialog } from 'src/components/confirmDialog/confirmDialog';
import { $generateHtmlFromNodes } from '@lexical/html';
import DOMPurify from 'dompurify';
import { ImageDialog } from '../ui/imageDialog';

const LowPriority = 1;

const PLACEHOLDERS = {
	firstName: '~FirstName~',
	lastName: '~LastName~',
	balance: '~Balance~',
	ageDays: '~AgeDays~',
	minAmount: '~MinAmount~',
	payLink: '~PayLink~',
};

/**
 * @typedef {object} ToolbarPluginProps
 *
 * @property {boolean} [richText] Whether or not show the rich text options in the toolbar.
 * @property {() => {}} onPlaceholderButtonClick The function to call when the placeholder button is clicked.
 */

/**
 * Reusable toolbar plugin for the editor.
 *
 * @param {ToolbarPluginProps} props The props for the toolbar plugin.
 */
export function ToolbarPlugin({ richText, onPlaceholderButtonClick }) {
	const [editor] = useLexicalComposerContext();
	const toolbarRef = useRef(null);

	const [canUndo, setCanUndo] = useState(false);
	const [canRedo, setCanRedo] = useState(false);

	const [isBold, setIsBold] = useState(false);
	const [isItalic, setIsItalic] = useState(false);
	const [isUnderline, setIsUnderline] = useState(false);
	const [isStrikethrough, setIsStrikethrough] = useState(false);

	const [selectedTextAlignment, setSelectedTextAlignment] = useState('left');

	const [placeholderDrawerOpen, setPlaceholderDrawerOpen] = useState(
		localStorage.getItem('showPlaceholders') === 'true',
	);
	const [showClearConfirmation, setShowClearConfirmation] = useState(false);
	const [showPreviewDialog, setShowPreviewDialog] = useState(false);
	const [showImageDialog, setShowImageDialog] = useState(false);

	const [previewContent, setPreviewContent] = useState('');

	const $updateToolbar = useCallback(() => {
		const selection = $getSelection();
		if (!$isRangeSelection(selection)) {
			return;
		}
		const node = getSelectedNode(selection);
		const parent = node.getParent();

		setIsBold(selection.hasFormat('bold'));
		setIsItalic(selection.hasFormat('italic'));
		setIsUnderline(selection.hasFormat('underline'));
		setIsStrikethrough(selection.hasFormat('strikethrough'));

		// Update alignment.
		let matchingParent;
		if ($isLinkNode(parent)) {
			// If node is a link, we need to fetch the parent paragraph node to set format.
			matchingParent = $findMatchingParent(
				node,
				(parentNode) => $isElementNode(parentNode) && !parentNode.isInline(),
			);
		}
		setSelectedTextAlignment(
			$isElementNode(matchingParent)
				? matchingParent.getFormatType()
				: $isElementNode(node)
					? node.getFormatType()
					: parent?.getFormatType() || 'left',
		);

		// Update links
		// const parent = node.getParent();
		// if ($isLinkNode(parent) || $isLinkNode(node)) {
		// 	setIsLink(true);
		// } else {
		// 	setIsLink(false);
		// }

		// Handle buttons
		// setFontColor(
		// 	$getSelectionStyleValueForProperty(selection, 'color', '#000'),
		// );
	}, []);

	useEffect(() => {
		return mergeRegister(
			editor.registerUpdateListener(({ editorState }) => {
				editorState.read(() => {
					$updateToolbar();
				});
			}),
			editor.registerCommand(
				SELECTION_CHANGE_COMMAND,
				(_payload, _newEditor) => {
					$updateToolbar();
					return false;
				},
				LowPriority,
			),
			editor.registerCommand(
				CAN_UNDO_COMMAND,
				(payload) => {
					setCanUndo(payload);
					return false;
				},
				LowPriority,
			),
			editor.registerCommand(
				CAN_REDO_COMMAND,
				(payload) => {
					setCanRedo(payload);
					return false;
				},
				LowPriority,
			),
		);
	}, [editor, $updateToolbar]);

	/**
	 * Generates a preview of the message content.
	 */
	function generatePreview() {
		editor.update(() => {
			let content = '';
			if (richText) {
				const htmlString = $generateHtmlFromNodes(editor);
				content = DOMPurify.sanitize(htmlString);
			} else {
				content = $getRoot().getTextContent();
			}
			const replacedString = replacePlaceholders(content);
			setPreviewContent(replacedString);
		});
	}

	/**
	 * Replaces placeholders in the content with example values.
	 *
	 * @param {string} contentWithPlaceholders The content with placeholders to replace.
	 * @returns {string} The content with placeholders replaced.
	 */
	function replacePlaceholders(contentWithPlaceholders) {
		return contentWithPlaceholders
			.replaceAll(PLACEHOLDERS.firstName, 'Bruce')
			.replaceAll(PLACEHOLDERS.lastName, 'Wayne')
			.replaceAll(PLACEHOLDERS.balance, '434.76')
			.replaceAll(PLACEHOLDERS.ageDays, '20')
			.replaceAll(PLACEHOLDERS.minAmount, '282.60')
			.replaceAll(
				PLACEHOLDERS.payLink,
				'https://customerdynamics.com/samplepaylink',
			);
	}

	return (
		<>
			<div className="toolbar" ref={toolbarRef}>
				{/* History Controls */}
				{/* Undo */}
				<Tooltip title="Undo" enterDelay={1000} arrow>
					<div>
						<IconButton
							size="small"
							disabled={!canUndo}
							onClick={() => {
								editor.dispatchCommand(UNDO_COMMAND, undefined);
							}}
							aria-label="Undo"
						>
							<Undo />
						</IconButton>
					</div>
				</Tooltip>

				{/* Redo */}
				<Tooltip title="Redo" enterDelay={1000} arrow>
					<div>
						<IconButton
							size="small"
							disabled={!canRedo}
							onClick={() => {
								editor.dispatchCommand(REDO_COMMAND, undefined);
							}}
							aria-label="Redo"
						>
							<Redo />
						</IconButton>
					</div>
				</Tooltip>

				<Divider orientation="vertical" flexItem />

				{/* Format Options */}
				{richText && (
					<>
						{/* Bold */}
						<Tooltip title="Bold" enterDelay={1000} arrow>
							<IconButton
								size="small"
								color={isBold ? 'primary' : 'default'}
								onClick={() => {
									editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
								}}
								className={isBold ? 'toolbar-active' : ''}
								aria-label="Bold"
							>
								<FormatBold />
							</IconButton>
						</Tooltip>

						{/* Italic */}
						<Tooltip title="Italic" enterDelay={1000} arrow>
							<IconButton
								size="small"
								color={isItalic ? 'primary' : 'default'}
								onClick={() => {
									editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
								}}
								className={isItalic ? 'toolbar-active' : ''}
								aria-label="Italics"
							>
								<FormatItalic />
							</IconButton>
						</Tooltip>

						{/* Underline */}
						<Tooltip title="Underline" enterDelay={1000} arrow>
							<IconButton
								size="small"
								color={isUnderline ? 'primary' : 'default'}
								onClick={() => {
									editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
								}}
								className={isUnderline ? 'toolbar-active' : ''}
								aria-label="Underline"
							>
								<FormatUnderlined />
							</IconButton>
						</Tooltip>

						{/* Strikethrough */}
						<Tooltip title="Strikethrough" enterDelay={1000} arrow>
							<IconButton
								size="small"
								color={isStrikethrough ? 'primary' : 'default'}
								onClick={() => {
									editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
								}}
								className={isStrikethrough ? 'toolbar-active' : ''}
								aria-label="Strikethrough"
							>
								<FormatStrikethrough />
							</IconButton>
						</Tooltip>

						<Divider orientation="vertical" flexItem />
					</>
				)}

				{/* Text Alignment */}
				{richText && (
					<>
						{/* Align Left */}
						<Tooltip title="Align Left" enterDelay={1000} arrow>
							<IconButton
								size="small"
								color={selectedTextAlignment === 'left' ? 'primary' : 'default'}
								onClick={() => {
									editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
								}}
								className={
									selectedTextAlignment === 'left' ? 'toolbar-active' : ''
								}
								aria-label="Align Left"
							>
								<FormatAlignLeft />
							</IconButton>
						</Tooltip>

						{/* Align Center */}
						<Tooltip title="Center" enterDelay={1000} arrow>
							<IconButton
								size="small"
								color={
									selectedTextAlignment === 'center' ? 'primary' : 'default'
								}
								onClick={() => {
									editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
								}}
								className={
									selectedTextAlignment === 'center' ? 'toolbar-active' : ''
								}
								aria-label="Center"
							>
								<FormatAlignCenter />
							</IconButton>
						</Tooltip>

						{/* Align Right */}
						<Tooltip title="Align Right" enterDelay={1000} arrow>
							<IconButton
								size="small"
								color={
									selectedTextAlignment === 'right' ? 'primary' : 'default'
								}
								onClick={() => {
									editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
								}}
								className={
									selectedTextAlignment === 'right' ? 'toolbar-active' : ''
								}
								aria-label="Align Right"
							>
								<FormatAlignRight />
							</IconButton>
						</Tooltip>

						{/* Justify Align */}
						<Tooltip title="Justify" enterDelay={1000} arrow>
							<IconButton
								size="small"
								color={
									selectedTextAlignment === 'justify' ? 'primary' : 'default'
								}
								onClick={() => {
									editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
								}}
								className={
									selectedTextAlignment === 'justify' ? 'toolbar-active' : ''
								}
								aria-label="Justify"
							>
								<FormatAlignJustify />
							</IconButton>
						</Tooltip>

						<Divider orientation="vertical" flexItem />

						{/* Decrease Indent */}
						<Tooltip title="Decrease Indent" enterDelay={1000} arrow>
							<IconButton
								size="small"
								onClick={() => {
									editor.dispatchCommand(OUTDENT_CONTENT_COMMAND);
								}}
								aria-label="Decrease Indent"
							>
								<FormatIndentDecrease />
							</IconButton>
						</Tooltip>

						{/* Increase Indent */}
						<Tooltip title="Increase Indent" enterDelay={1000} arrow>
							<IconButton
								size="small"
								onClick={() => {
									editor.dispatchCommand(INDENT_CONTENT_COMMAND);
								}}
								aria-label="Increase Indent"
							>
								<FormatIndentIncrease />
							</IconButton>
						</Tooltip>

						<Divider orientation="vertical" flexItem />
					</>
				)}

				{/* Special Elements */}

				{richText && (
					<>
						{/* Ordered List */}
						<Tooltip title="Ordered List" enterDelay={1000} arrow>
							<IconButton
								size="small"
								onClick={() => {
									editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND);
								}}
								aria-label="Ordered List"
							>
								<FormatListNumbered />
							</IconButton>
						</Tooltip>

						{/* Unordered List */}
						<Tooltip title="Unordered List" enterDelay={1000} arrow>
							<IconButton
								size="small"
								onClick={() => {
									editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND);
								}}
								aria-label="Unordered List"
							>
								<FormatListBulleted />
							</IconButton>
						</Tooltip>

						{/* Link */}
						<Tooltip title="Link" enterDelay={1000} arrow>
							<IconButton
								size="small"
								onClick={() => {
									editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
								}}
								aria-label="Link"
							>
								<Link />
							</IconButton>
						</Tooltip>

						{/* Image */}
						<Tooltip title="Image" enterDelay={1000} arrow>
							<IconButton
								size="small"
								onClick={() => {
									setShowImageDialog(true);
								}}
								aria-label="Image"
							>
								<Image />
							</IconButton>
						</Tooltip>
					</>
				)}

				<Tooltip title="Placeholders" enterDelay={1000} arrow>
					<IconButton
						size="small"
						color={placeholderDrawerOpen ? 'primary' : 'default'}
						onClick={() => {
							onPlaceholderButtonClick();
							setPlaceholderDrawerOpen(
								localStorage.getItem('showPlaceholders') === 'true',
							);
						}}
						className={placeholderDrawerOpen ? 'toolbar-active' : ''}
						aria-label="Placeholders"
					>
						<Article />
					</IconButton>
				</Tooltip>

				<Divider orientation="vertical" flexItem />

				{/* Other */}

				{/* Preview */}
				<Tooltip title="Preview Message" enterDelay={1000} arrow>
					<IconButton
						size="small"
						onClick={() => {
							generatePreview();
							setShowPreviewDialog(true);
						}}
						aria-label="Preview"
					>
						<Preview />
					</IconButton>
				</Tooltip>

				{/* Clear */}
				<Tooltip title="Clear" enterDelay={1000} arrow>
					<IconButton
						size="small"
						onClick={() => {
							setShowClearConfirmation(true);
						}}
						aria-label="Clear"
					>
						<Clear />
					</IconButton>
				</Tooltip>
			</div>
			<Divider />

			{/* Clear Confirmation */}
			<ConfirmDialog
				open={showClearConfirmation}
				title="Clear Message Template"
				description="Are you sure you want to clear this message template?"
				actionOneText="Clear"
				actionOneHandler={() => {
					editor.dispatchCommand(CLEAR_EDITOR_COMMAND);
					setShowClearConfirmation(false);
				}}
				actionTwoText="Cancel"
				actionTwoHandler={() => {
					setShowClearConfirmation(false);
				}}
			/>

			{/* Insert Image Dialog */}
			<ImageDialog
				open={showImageDialog}
				onClose={(imageDetails) => {
					if (imageDetails) {
						editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
							altText: imageDetails.description,
							// height: 100,
							// width: 100,
							src: imageDetails.url,
						});
					}
					setShowImageDialog(false);
				}}
			/>

			{/* Message Preview Dialog */}
			<Dialog
				open={showPreviewDialog}
				onClose={() => {
					setShowPreviewDialog(false);
				}}
				fullScreen
			>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: '1fr auto',
						alignItems: 'start',
					}}
				>
					<DialogTitle>Message Preview</DialogTitle>
					<IconButton
						color="primary"
						onClick={() => {
							setShowPreviewDialog(false);
						}}
					>
						<Close />
					</IconButton>
				</div>
				<DialogContent className="plain-text-dialog">
					{!richText && <DialogContentText>{previewContent}</DialogContentText>}
					{richText && (
						<div dangerouslySetInnerHTML={{ __html: previewContent }} />
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}
